import React, { useCallback, useEffect, useState } from 'react'
import { Box, Container, Flex, Heading, Text, VStack } from '@chakra-ui/react'
import { Proto, SharedCodec } from '@probe/shared'
import useSessionStorageState from 'use-session-storage-state'
import { UserContext } from './auth/user-context'
import { CommunicatorContext } from './communication/communicator-context'
import { CommunicatorImpl } from './communication/communicator-impl'
import { WebSocketManager } from './communication/web-socket-manager'
import { LoginGoogle } from './login/google-login'
import { Login } from './login/login'
import { NotificationRenderer, useNotification } from './main/components/notifications'
import { Layout } from './main/layout'
import { Route, Router, Routes } from 'react-router-dom'
import { Download } from './download'

const sharedCodec = SharedCodec

export const App = () => {
    const [communicator] = useState(new CommunicatorImpl(new WebSocketManager(80), sharedCodec))
    const [authenticatedUser, setAuthenticatedUser] = useState<Proto.Users.User | undefined>()
    const [errorTimeout, setErrorTimeout] = useState<ReturnType<typeof setTimeout> | undefined>()

    const [token, setToken] = useSessionStorageState<string | undefined>('token')

    useEffect(() => {
        communicator.start()

        if (token) {
            communicator
                .doRequest(new Proto.Authentication.AuthenticationRequest('token', { token }))
                .then((response) => {
                    if (response instanceof Proto.Authentication.AuthenticationOkResponse) {
                        setAuthenticatedUser(response.user)
                    } else {
                        setToken(undefined)
                    }
                })
        }

        return () => {
            return communicator.stop()
        }
    }, [])

    const notification = useNotification()

    const handleLogin = useCallback(
        (type: string, data: unknown) => {
            setErrorTimeout(undefined)
            if (errorTimeout) {
                clearTimeout(errorTimeout)
            }

            return communicator
                .doRequest(new Proto.Authentication.AuthenticationRequest(type, data))
                .then((response) => {
                    if (response instanceof Proto.Authentication.AuthenticationOkResponse) {
                        setAuthenticatedUser(response.user)
                        setToken(response.token)
                    } else if (
                        response instanceof Proto.Authentication.AuthenticationFailedResponse
                    ) {
                        notification({
                            type: 'error',
                            message: response.message
                        })
                    }
                })
        },
        [communicator, errorTimeout, notification, setToken]
    )

    return (
        <CommunicatorContext.Provider value={communicator}>
            <Box minH="100vh">
                {!authenticatedUser && !token && (
                    <Flex minH="100vh" justify="center" align="center">
                        <Container maxW="md">
                            <Routes>
                                <Route path="/download" element={<Download />} />
                                <Route
                                    path="*"
                                    element={
                                        <VStack align="stretch" gap={6}>
                                            <Box>
                                                <Heading size="lg">
                                                    PR0BE
                                                    <Text
                                                        as="span"
                                                        ml={2}
                                                        fontSize="18px"
                                                        fontWeight="normal"
                                                    >
                                                        ACCOUNTS
                                                    </Text>
                                                </Heading>
                                            </Box>

                                            <Box>
                                                <Login.Component onLogin={handleLogin} />
                                            </Box>

                                            <Box>
                                                <LoginGoogle onLogin={handleLogin} />
                                            </Box>
                                        </VStack>
                                    }
                                />
                            </Routes>
                        </Container>
                    </Flex>
                )}

                {authenticatedUser && (
                    <UserContext.Provider value={authenticatedUser}>
                        <Layout.Component
                            onLogout={() => {
                                setAuthenticatedUser(undefined)
                                setToken(undefined)
                            }}
                        />
                    </UserContext.Provider>
                )}

                <NotificationRenderer />
            </Box>
        </CommunicatorContext.Provider>
    )
}
